import React from "react";
import styled from "styled-components";
import CopyButton from "@src/components/CopyButton";
import { useShowDetailsQuery } from "@src/queries/showQueries";

interface ShowDetailsTooltipProps {
  showDocumentId: string;
  /** Only fetch once the user has hovered the anchor (lazy on hover). */
  enabled: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 180px;
  max-width: 260px;
  font-size: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Label = styled.span`
  color: ${({ theme }) => theme.colors.gray300};
  font-weight: 600;
`;

const ValueGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Value = styled.span`
  color: ${({ theme }) => theme.colors.white};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.gray300};
`;

interface DetailRowProps {
  label: string;
  value: string | null;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <Row>
    <Label>{label}</Label>
    {value ? (
      <ValueGroup>
        <Value title={value}>{value}</Value>
        <CopyButton value={value} ariaLabel={`Copy ${label.toLowerCase()}`} />
      </ValueGroup>
    ) : (
      <Value>—</Value>
    )}
  </Row>
);

const ShowDetailsTooltip: React.FC<ShowDetailsTooltipProps> = ({
  showDocumentId,
  enabled,
}) => {
  const { data, isLoading, isError } = useShowDetailsQuery(showDocumentId, {
    enabled: enabled && !!showDocumentId,
  });

  if (!enabled || isLoading) return <Message>Loading details…</Message>;
  if (isError || !data) return <Message>Could not load details.</Message>;

  const hasAnyDetail = data.frequency || data.email || data.phone;
  if (!hasAnyDetail) return <Message>No details available.</Message>;

  return (
    <Container>
      <DetailRow label="Frequency" value={data.frequency} />
      <DetailRow label="Email" value={data.email} />
      <DetailRow label="Phone" value={data.phone} />
    </Container>
  );
};

export default ShowDetailsTooltip;
